import React, { useEffect } from 'react';

const SITE_NAME = 'Bizani Media';
const DEFAULT_IMAGE = '/logo/New%20Bizani%20logo.png';
const BASE_URL = 'https://www.bizani.co.zw';
const DEFAULT_KEYWORDS = 'Bizani Media, digital storytelling, documentaries, branding, Zimbabwe, media production, events coverage, video production';

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  path?: string;
  keywords?: string;
  noIndex?: boolean;
  /** Optional JSON-LD structured data (object or array of objects). */
  jsonLd?: object | object[];
}

/** Updates document title, meta tags, and structured data for SEO and social sharing. */
const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image = DEFAULT_IMAGE,
  path = '',
  keywords = DEFAULT_KEYWORDS,
  noIndex = false,
  jsonLd
}) => {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const origin = typeof window !== 'undefined' ? window.location.origin : BASE_URL;
    const canonicalUrl = path ? `${origin}${path}` : origin;
    const imageUrl = image.startsWith('http') ? image : `${origin}${image}`;
    const desc = description.replace(/\s+/g, ' ').trim().slice(0, 160);

    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    if (keywords) setMeta('keywords', keywords);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', desc, true);
    setMeta('og:image', imageUrl, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:type', 'website', true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:locale', 'en_ZW', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', imageUrl);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    if (noIndex) {
      setMeta('robots', 'noindex, nofollow');
    }

    // Remove existing SEO JSON-LD scripts
    document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());

    const scripts: object[] = [];

    // Organization (all pages)
    scripts.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: BASE_URL,
      logo: `${BASE_URL}${DEFAULT_IMAGE}`,
      description: 'Digital storytelling, strategy and innovation. Documentary production, branding, events coverage, and project documentation across Zimbabwe.',
      email: 'info@bizani.co.zw',
      sameAs: [
        'https://www.linkedin.com/company/bizanimedia',
        'https://www.facebook.com/bizanimedia',
        'https://www.instagram.com/bizanimedia',
        'https://www.youtube.com/@bizanimedia',
        'https://twitter.com/bizanimedia'
      ],
      address: { '@type': 'PostalAddress', addressLocality: 'Avonlea, Harare', addressCountry: 'ZW' }
    });

    // WebSite (all pages)
    scripts.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: BASE_URL,
      description: desc,
      publisher: { '@id': `${BASE_URL}/#organization` }
    });

    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      scripts.push(...arr);
    }

    scripts.forEach((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });
  }, [title, description, image, path, keywords, noIndex, jsonLd]);

  return null;
};

export default SEO;
